
import { SMA, EMA, BollingerBands, RSI, VWAP, ATR } from 'technicalindicators';

// --- Helper: Convert OHLCV Array for technicalindicators ---
// technicalindicators usually expects arrays of numbers: [close, close, ...] or objects {high:[], low:[], ...}
const getInputData = (data, key = 'close') => data.map(d => d[key]);

const getHighLowClose = (data) => ({
    high: data.map(d => d.high),
    low: data.map(d => d.low),
    close: data.map(d => d.close),
    volume: data.map(d => d.volume),
});

/**
 * Calculate Moving Average (SMA)
 * @param {Array} data - Array of OHLCV objects
 * @param {number} period - Period
 * @returns {Array} - Array of { time, value } for lightweight-charts
 */
export const calculateSMA = (data, period) => {
    if (data.length < period) return [];

    const input = {
        values: getInputData(data, 'close'),
        period: period
    };
    const results = SMA.calculate(input);

    // technicalindicators returns result array smaller than input by (period - 1).
    // We need to align timestamps. The result[0] corresponds to input[period - 1].
    return results.map((val, i) => {
        const dataPoint = data[i + period - 1];
        if (!dataPoint) return null;
        return {
            time: dataPoint.time,
            value: val
        };
    }).filter(item => !!item);
};

/**
 * Calculate Exponential Moving Average (EMA)
 */
export const calculateEMA = (data, period) => {
    if (data.length < period) return [];

    const input = {
        values: getInputData(data, 'close'),
        period: period
    };
    const results = EMA.calculate(input);

    return results.map((val, i) => {
        const dataPoint = data[i + period - 1];
        if (!dataPoint) return null;
        return {
            time: dataPoint.time,
            value: val
        };
    }).filter(item => !!item);
};

export const calculateBollinger = (data, period = 20, stdDev = 2) => {
    if (data.length < period) return { upper: [], lower: [], middle: [] };

    const input = {
        period: period,
        values: getInputData(data, 'close'),
        stdDev: stdDev
    };
    const results = BollingerBands.calculate(input);

    const upper = [];
    const lower = [];
    const middle = [];

    results.forEach((res, i) => {
        const dataPoint = data[i + period - 1];
        if (dataPoint) {
            const time = dataPoint.time;
            upper.push({ time, value: res.upper });
            lower.push({ time, value: res.lower });
            middle.push({ time, value: res.middle });
        }
    });

    return { upper, lower, middle };
};

export const calculateRSI = (data, period = 14) => {
    if (data.length < period) return [];

    const input = {
        values: getInputData(data, 'close'),
        period: period
    };
    const results = RSI.calculate(input);

    return results.map((val, i) => {
        // RSI calculation consumes 'period' items?
        // technicalindicators RSI result length = input - period
        // Index mapping: result[0] is for input[period]
        const dataPoint = data[i + period];
        if (!dataPoint) return null;
        return {
            time: dataPoint.time,
            value: val
        };
    }).filter(item => !!item);
};

export const calculateVWAP = (data) => {
    const input = {
        high: data.map(d => d.high),
        low: data.map(d => d.low),
        close: data.map(d => d.close),
        volume: data.map(d => d.volume)
    };

    const results = VWAP.calculate(input);

    return results.map((val, i) => {
        const dataPoint = data[i];
        if (!dataPoint) return null;
        return {
            time: dataPoint.time,
            value: val
        };
    }).filter(item => !!item);
};

/**
 * Calculate Moving VWAP (Rolling Window)
 * @param {Array} data - Array of OHLCV objects
 * @param {number} period - Rolling VWAP period
 */
export const calculateMovingVWAP = (data, period) => {
    if (data.length < period) return [];

    const results = [];

    // Calculate for each valid window
    for (let i = period - 1; i < data.length; i++) {
        let sumPV = 0;
        let sumV = 0;

        // Summing up (Typical Price * Volume) and Volume over the period
        for (let j = 0; j < period; j++) {
            const d = data[i - j];
            const typicalPrice = (d.high + d.low + d.close) / 3;
            sumPV += typicalPrice * d.volume;
            sumV += d.volume;
        }

        const vwap = sumV === 0 ? data[i].close : sumPV / sumV;

        results.push({
            time: data[i].time,
            value: vwap
        });
    }

    return results;
};


/**
 * Calculate True Baseline Median SuperTrend
 * Uses Median of HL2 as baseline and ATR for volatility bands.
 * @param {Array} data - Array of OHLCV objects
 * @param {number} period - ATR and Median Period
 * @param {number} multiplier - ATR Multiplier
 */
export const calculateTBMSuperTrend = (data, period = 10, multiplier = 3) => {
    if (data.length <= period) return [];

    const atrInput = {
        high: data.map(d => d.high),
        low: data.map(d => d.low),
        close: data.map(d => d.close),
        period: period
    };

    let atrResults;
    try {
        atrResults = ATR.calculate(atrInput);
    } catch (e) {
        console.error("Failed to calculate ATR", e);
        return [];
    }

    const tbmData = [];

    let finalUpper = 0;
    let finalLower = 0;
    let trend = 1; // 1 = Up, -1 = Down

    const getMedian = (arr) => {
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
    };

    const hl2 = data.map(d => (d.high + d.low) / 2);

    for (let i = period; i < data.length; i++) {
        // technicalindicators Output mapped to exact index (delayed by period)
        const atrIndex = i - period;
        const atr = atrResults[atrIndex];
        if (atr === undefined) continue;

        const window = hl2.slice(i - period + 1, i + 1);
        const baseline = getMedian(window);

        const basicUpper = baseline + multiplier * atr;
        const basicLower = baseline - multiplier * atr;

        const currentClose = data[i].close;
        const prevClose = data[i - 1].close;

        if (i === period) {
            finalUpper = basicUpper;
            finalLower = basicLower;
            trend = currentClose > finalUpper ? 1 : -1;
        } else {
            // Final Upper Band
            if (prevClose <= finalUpper) {
                finalUpper = Math.min(basicUpper, finalUpper);
            } else {
                finalUpper = basicUpper;
            }

            // Final Lower Band
            if (prevClose >= finalLower) {
                finalLower = Math.max(basicLower, finalLower);
            } else {
                finalLower = basicLower;
            }

            // Trend inversion
            if (trend === -1 && currentClose > finalUpper) {
                trend = 1;
            } else if (trend === 1 && currentClose < finalLower) {
                trend = -1;
            }
        }

        const stValue = trend === 1 ? finalLower : finalUpper;
        const time = data[i].time;

        tbmData.push({ time, value: stValue, trend });
    }

    return tbmData;
};
